/**
 * PWA Setup Module (JavaScript)
 * 
 * Safely initializes PWA features:
 * - Service worker registration (production only)
 * - Install prompt capture (optional)
 * - Graceful error handling
 */

let installPromptEvent = null;

/**
 * Register service worker in production only
 * In development, the dev server handles SW registration
 */
export async function registerServiceWorker() {
  // Only register in production (when SW is built)
  if (process.env.NODE_ENV !== 'production' && !process.env.REACT_APP_ENABLE_SW) {
    console.log('[PWA] Service worker registration disabled in development');
    return;
  }

  // Check browser support
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service Worker API not supported in this browser');
    return;
  }

  try {
    console.log('[PWA] Registering service worker...');
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });

    console.log('[PWA] Service Worker registered:', registration);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated') {
            console.log('[PWA] Service Worker updated and ready');
            // Optionally notify user of app update
            notifyAppUpdate();
          }
        });
      }
    });
  } catch (error) {
    console.error('[PWA] Service Worker registration failed:', error);
    // Fail gracefully; app continues to work
  }
}

/**
 * Setup install prompt handling
 * Must be called early (during app initialization)
 */
export function setupInstallPrompt(onPromptReady) {
  // Capture the beforeinstallprompt event
  function handleBeforeInstallPrompt(event) {
    // Prevent the mini-infobar from appearing on mobile
    event.preventDefault();
    installPromptEvent = event;
    console.log('[PWA] Install prompt ready');
    onPromptReady(true);
  }

  // Listen for app installed event
  function handleAppInstalled() {
    console.log('[PWA] App installed successfully');
    installPromptEvent = null;
    onPromptReady(false);
  }

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', handleAppInstalled);
  };
}

/**
 * Trigger the install prompt programmatically
 * Only works if beforeinstallprompt event was captured
 */
export async function triggerInstallPrompt() {
  if (!installPromptEvent) {
    console.warn('[PWA] Install prompt not ready');
    return false;
  }

  try {
    installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;
    console.log(`[PWA] User response to install prompt: ${outcome}`);

    // Clear the prompt event after user interacts
    installPromptEvent = null;
    return outcome === 'accepted';
  } catch (error) {
    console.error('[PWA] Error triggering install prompt:', error);
    return false;
  }
}

/**
 * Check if app is running in standalone (installed) mode
 */
export function isStandalone() {
  // Check for standalone mode indicators
  const isStandaloneMode =
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS
    window.navigator.standalone === true;

  return isStandaloneMode;
}

/**
 * Notify user of app update
 * (Optional: integrate with your toast/notification system)
 */
function notifyAppUpdate() {
  // Emit custom event that UI can listen to
  window.dispatchEvent(
    new CustomEvent('pwa-update-ready', {
      detail: { message: 'App update available. Please refresh.' },
    })
  );
}

/**
 * Check PWA installation status
 */
export async function checkPWAStatus() {
  const hasSW =
    'serviceWorker' in navigator &&
    (await navigator.serviceWorker.getRegistrations().then(r => r.length > 0));

  const hasManifest = document.querySelector('link[rel="manifest"]') !== null;

  return {
    isStandalone: isStandalone(),
    hasSW,
    manifest: hasManifest,
  };
}
